<div *ngIf="visible" class="create-sequence-form">
  <p-card>
    <ng-template pTemplate="header">
      <div class="form-header m-0 p-0">
        <h2>{{ editingSequence ? 'Edit Email Sequence' : 'Create Email Sequence' }}</h2>
        <div class="form-actions m-0 p-0">
          <button pButton pRipple label="Cancel" class="p-button-text mr-2"
            (click)="cancelSequenceCreation()"></button>
          <button pButton pRipple label="{{ editingSequence ? 'Update Sequence' : 'Save Sequence' }}"
            class="p-button-primary" (click)="saveSequence()" [loading]="isSaving"></button>
        </div>
      </div>
    </ng-template>
    
    <div class="flex justify-content-start">
      <h4 class="m-0 p-0">Platform:</h4>
      @if(getEmailIntegrations().length > 1) {
        <p-select class="m-o p-0" [(ngModel)]="selectedIntegration" optionLabel="platform" [options]="getEmailIntegrations()"
          (onChange)="onIntegrationChange()" placeholder="Select an Email Platform">
        </p-select>
      } @else {
       <p class="ml-2 mt-0 p-0"> <i>{{selectedIntegration?.platform || '' | capitalizeWords}}</i></p>
      }
    </div>
    
    <div class="form-content">
      <div class="p-grid p-formgrid">
        <div class="p-col-6">
          <div class="p-field">
            <label for="sequenceName">Sequence Name</label>
            <input id="sequenceName" type="text" pInputText [(ngModel)]="newSequence.name"
              placeholder="Enter sequence name" class="p-inputtext-lg w-full">
          </div>
        </div>
        <div class="p-col-6 mt-2">
          <div class="p-field">
            <label for="sequenceDescription">Description (Optional)</label>
            <input id="sequenceDescription" type="text" pInputText [(ngModel)]="newSequence.description"
              placeholder="Briefly describe this sequence" class="p-inputtext-lg w-full">
          </div>
        </div>
        <div class="p-col-6 mt-2">
          <div class="p-field">
            <label for="industrySelect">Industry</label>
            <p-dropdown id="industrySelect" [options]="industries" [(ngModel)]="newSequence.industry"
              placeholder="Select an industry" optionLabel="label" optionValue="value" 
              class="p-inputtext-lg w-full" [showClear]="true"></p-dropdown>
          </div>
        </div>
        <div class="p-col-6 mt-2">
          <div class="p-field">
            <label for="prospects">Target Prospect List
              <i class="pi pi-question-circle ml-1 text-gray-500"
                pTooltip="Create the sequence with a predefined prospect list if available" tooltipPosition="top">
              </i>
            </label>
            <p-dropdown [options]="prospects" name="prospects" [(ngModel)]="newSequence.prospectListId"
              optionLabel="name" placeholder="Select prospect list" [style]="{'width':'100%'}"
              [disabled]="prospects.length==0"></p-dropdown>
          </div>
        </div>
        <div class="p-col-6 mt-2">
          <div class="p-field">
            <label for="prospects">Platform Email Accounts *</label>
            <p-multiselect [options]="platformEmailAccounts" [(ngModel)]="selectedPlatformEmailAccounts"
              placeholder="Select Email Accounts" optionLabel="email" display="chip" class="w-full md:w-80"
              [class.ng-invalid]="isEmailAccountsInvalid && hasTriedToSave" (onChange)="onEmailAccountsChange()" />
            
            <small class="p-error" *ngIf="isEmailAccountsInvalid && hasTriedToSave">
              Please select at least one email account
            </small>
          </div>
        </div>
      </div>

      <div class="sequence-steps mt-4">
        <h3>Sequence Steps</h3>

        <!-- Initial Email Step -->
        <p-panel styleClass="mb-3">
          <ng-template pTemplate="header">
            <div class="step-header">
              <div class="step-number">1</div>
              <div class="step-title">Initial Email</div>
            </div>
          </ng-template>
          <ng-template pTemplate="icons">
            <button pButton pRipple icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
              (click)="removeStep(0)"></button>
          </ng-template>

          <div class="p-field mb-3">
            <label>Email Template</label>
            <p-dropdown [options]="emailTemplates" [(ngModel)]="newSequence.steps[0].templateId"
              placeholder="Select a template" styleClass="w-full"></p-dropdown>
          </div>

          <div class="ab-testing-section">
            <div class="ab-testing-toggle">
              <p-checkbox [(ngModel)]="newSequence.steps[0].abTestingEnabled" [binary]="true"
                inputId="abTesting1"></p-checkbox>
              <label for="abTesting1" class="ml-2">A/B Testing</label>
            </div>

            <div *ngIf="newSequence.steps[0].abTestingEnabled" class="ab-testing-config mt-3">
              <h4>A/B Testing Configuration</h4>
              <p>What would you like to test?</p>

              <p-tabView>
                <p-tabPanel header="Subject Line">
                  <div class="p-field">
                    <label>Variant B Subject Line</label>
                    <input type="text" pInputText [(ngModel)]="newSequence.steps[0].abVariantSubject"
                      placeholder="Enter alternative subject line" class="w-full">
                    <small class="text-secondary">The system will automatically split your audience and test which
                      subject line performs better.</small>
                  </div>
                </p-tabPanel>
                <p-tabPanel header="Email Body">
                  <div class="p-field">
                    <label>Variant B Email Body</label>
                    <textarea type="text" pInputText [(ngModel)]="newSequence.steps[0].abVariantBody"
                      placeholder="Enter alternative Email Body" class="w-full" rows="4"></textarea>
                    <small class="text-secondary">Test different messaging approaches to see which resonates better
                      with your prospects.</small>
                  </div>
                </p-tabPanel>
                <p-tabPanel header="Call to Action">
                  <div class="p-field">
                    <label>Variant B Call To Action</label>
                    <input type="text" pInputText [(ngModel)]="newSequence.steps[0].abVariantCTA"
                      placeholder="Enter alternative Call To Action" class="w-full">
                    <small class="text-secondary">Test different calls to action to optimize for clicks and
                      conversions.</small>
                  </div>
                </p-tabPanel>
              </p-tabView>
            </div>
          </div>
        </p-panel>

        <!-- Follow-up Steps - Dynamically rendered -->
        <p-panel *ngFor="let step of getFollowUpSteps(); let i = index" styleClass="mb-3">
          <ng-template pTemplate="header">
            <div class="step-header">
              <div class="step-number">{{i + 2}}</div>
              <div class="step-title">Follow-up #{{i + 1}}</div>
            </div>
          </ng-template>
          <ng-template pTemplate="icons">
            <button pButton pRipple icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
              (click)="removeStep(i + 1)"></button>
          </ng-template>

          <div class="p-grid p-formgrid">
            <div class="p-col-6">
              <div class="p-field mb-3">
                <label>Wait Duration</label>
                <div class="p-inputgroup">
                  <p-inputNumber [(ngModel)]="step.waitDuration" [min]="1" [showButtons]="false"></p-inputNumber>
                  <p-dropdown [options]="timeUnits" [(ngModel)]="step.waitUnit"
                    styleClass="time-unit-dropdown"></p-dropdown>
                </div>
                <small class="text-secondary">Time to wait after previous step before sending this email</small>
              </div>
            </div>
          </div>

          <div class="p-field mb-3">
            <label>Email Template</label>
            <p-dropdown [options]="emailTemplates" [(ngModel)]="step.templateId" placeholder="Select a template"
              styleClass="w-full"></p-dropdown>
          </div>

          <div class="ab-testing-section">
            <div class="ab-testing-toggle">
              <p-checkbox [(ngModel)]="step.abTestingEnabled" [binary]="true"
                [inputId]="'abTesting' + (i + 2)"></p-checkbox>
              <label [for]="'abTesting' + (i + 2)" class="ml-2">A/B Testing</label>
            </div>

            <div *ngIf="step.abTestingEnabled" class="ab-testing-config mt-3">
              <h4>A/B Testing Configuration</h4>
              <p>What would you like to test?</p>

              <p-tabView>
                <p-tabPanel header="Subject Line">
                  <div class="p-field">
                    <label>Variant B Subject Line</label>
                    <input type="text" pInputText [(ngModel)]="step.abVariantSubject"
                      placeholder="Enter alternative subject line" class="w-full">
                    <small class="text-secondary">The system will automatically split your audience and test which
                      subject line performs better.</small>
                  </div>
                </p-tabPanel>
                <p-tabPanel header="Email Body">
                  <div class="p-field">
                    <label>Variant B Email Body</label>
                    <input type="text" pInputText [(ngModel)]="step.abVariantBody"
                      placeholder="Enter alternative Email Body" class="w-full">
                    <small class="text-secondary">Test different messaging approaches to see which resonates better
                      with your prospects.</small>
                  </div>
                </p-tabPanel>
                <p-tabPanel header="Call to Action">
                  <div class="p-field">
                    <label>Variant B Call To Action</label>
                    <input type="text" pInputText [(ngModel)]="step.abVariantCTA"
                      placeholder="Enter alternative Call To Action" class="w-full">
                    <small class="text-secondary">Test different calls to action to optimize for clicks and
                      conversions.</small>
                  </div>
                </p-tabPanel>
              </p-tabView>
            </div>
          </div>
        </p-panel>

        <div class="add-step text-center mt-4">
          <button pButton pRipple icon="pi pi-plus" label="Add Another Step" class="p-button-text"
            (click)="addSequenceStep()"></button>
        </div>
      </div>
    </div>

  </p-card>

  <!-- Loading overlay with progress stepper -->
  <div *ngIf="isSaving" class="loading-overlay">
    <div class="loading-content">
      <div class="save-progress-stepper">
        <div class="stepper-header">
          <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
          <h3 class="m-0 ml-3">Creating Your Sequence</h3>
        </div>

        <div class="stepper-steps">
          <div *ngFor="let step of saveSteps; let i = index"
               class="stepper-step"
               [class.completed]="step.completed"
               [class.active]="step.active">
            <div class="step-indicator">
              <i *ngIf="step.completed" class="pi pi-check"></i>
              <i *ngIf="step.active && !step.completed" class="pi pi-spin pi-spinner"></i>
              <span *ngIf="!step.completed && !step.active">{{ i + 1 }}</span>
            </div>
            <div class="step-content">
              <span class="step-label">{{ step.label }}</span>
              <span class="step-description">{{ step.description }}</span>
            </div>
          </div>
        </div>

        <p class="stepper-message text-color-secondary mt-3 mb-0">
          This may take a moment while we set up your campaign...
        </p>
      </div>
    </div>
  </div>
</div>

<!-- Lead Distribution Modal Overlay -->
<div *ngIf="showLeadDistribution && leadDistributionJobId" class="lead-distribution-overlay">
  <div class="lead-distribution-modal">
    <div class="modal-header">
      <h3 class="m-0">Distributing Leads</h3>
      <p class="text-color-secondary mt-2 mb-0">Your sequence has been created successfully. Now distributing leads to the platform...</p>
    </div>
    
    <div class="modal-content">
      <app-lead-distribution-status
        [jobId]="leadDistributionJobId"
        [pollInterval]="5000"
        (jobCompleted)="onLeadDistributionCompleted($event)"
        (jobFailed)="onLeadDistributionFailed($event)"
        (jobUpdated)="updateProgressFromJob($event)">
      </app-lead-distribution-status>
    </div>
  </div>
</div>